import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Mail, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PlungerLogo from '../PlungerLogo';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      // Update active section based on scroll position (only on landing page)
      if (location.pathname === '/') {
        const sections = ['home', 'technology', 'platform', 'results'];
        for (const section of sections.reverse()) {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= 150) {
              setActiveSection(section);
              break;
            }
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', section: 'home' },
    { name: 'Economics', section: 'technology' },
    { name: 'Solution', section: 'platform' },
    { name: 'Results', section: 'results' },
    { name: 'About', href: 'https://qult.ai', external: true },
  ];

  const scrollToSection = (sectionId) => {
    // If we're not on the landing page, navigate there first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const isOnLandingPage = location.pathname === '/';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 h-16 sm:h-20 transition-all duration-300 ${
      isScrolled
        ? 'bg-background-dark/85 backdrop-blur-2xl border-b border-white/10 shadow-2xl shadow-black/20'
        : 'bg-background-dark/55 backdrop-blur-xl border-b border-white/5'
    }`}>
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 h-full flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 sm:gap-3 text-white group">
          <div className="size-7 sm:size-8 transition-transform group-hover:scale-110 drop-shadow-[0_0_18px_rgba(230,126,34,0.35)]">
            <PlungerLogo className="w-7 h-7 sm:w-8 sm:h-8" />
          </div>
          <div className="flex flex-col">
            <h2 className="text-white text-lg sm:text-xl font-bold tracking-tight font-heading leading-none">
              PlungerXcel
            </h2>
            <p className="text-[10px] sm:text-xs text-gray-400 font-light">A product of Qult Technologies</p>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {navLinks.map((link) => (
            link.external ? (
              <a
                key={link.name}
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-medium transition-colors whitespace-nowrap text-gray-300 hover:text-white relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-primary after:transition-all hover:after:w-full"
              >
                {link.name}
              </a>
            ) : (
              <button
                key={link.section}
                onClick={() => scrollToSection(link.section)}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  isOnLandingPage && activeSection === link.section
                    ? 'text-primary'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {link.name}
              </button>
            )
          ))}

        </div>

        {/* CTA Button */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="mailto:tal.alexander@qult.ai"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-2 text-sm font-medium text-gray-200 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all"
          >
            <Mail className="w-4 h-4" />
            {/* Email */}
          </a>

          <a
            href="https://wa.me/923134832766"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-2 text-sm font-medium text-gray-200 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all"
          >
            <Phone className="w-4 h-4" />
            {/* WhatsApp */}
          </a>
          <Link
            to="/schedule-a-demo"
            className="flex items-center justify-center rounded-full h-10 px-5 bg-primary hover:bg-primary-hover transition-all hover:scale-[1.02] text-white text-sm font-bold tracking-wide shadow-lg shadow-primary/20"
          >
            Schedule Demo
          </Link>
        </div>

        {/* Mobile Menu Icon */}
        <button
          className="lg:hidden text-white p-2 -mr-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-background-dark/95 backdrop-blur-2xl border-b border-white/10 overflow-hidden"
          >
            <div className="px-4 sm:px-6 py-4 space-y-1">
              {navLinks.map((link, index) => (
                link.external ? (
                  <motion.a
                    key={link.name}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="block w-full text-left py-3 px-3 rounded-lg transition-colors text-gray-300 hover:text-primary hover:bg-white/5"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </motion.a>
                ) : (
                  <motion.button
                    key={link.section}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => scrollToSection(link.section)}
                    className={`block w-full text-left py-3 px-3 rounded-lg transition-colors ${
                      isOnLandingPage && activeSection === link.section
                        ? 'text-primary bg-primary/10'
                        : 'text-gray-300 hover:text-primary hover:bg-white/5'
                    }`}
                  >
                    {link.name}
                  </motion.button>
                )
              ))}
              <div className="grid grid-cols-2 gap-2 pt-2">
                <a
                  href="mailto:tal.alexander@qult.ai"
                  className="inline-flex items-center justify-center gap-2 rounded-lg py-3 bg-white/5 border border-white/10 text-white font-semibold hover:bg-white/10 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Mail className="w-4 h-4" />
                  Email
                </a>
                <a
                  href="https://wa.me/923134832766"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-lg py-3 bg-white/5 border border-white/10 text-white font-semibold hover:bg-white/10 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Phone className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: navLinks.length * 0.05 }}
                className="pt-2"
              >
                <Link
                  to="/schedule-a-demo"
                  className="block w-full text-center rounded-full py-3 bg-primary hover:bg-primary-hover transition-colors text-white font-bold shadow-lg shadow-primary/20"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Schedule Demo
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
